import { LinearGradient } from 'expo-linear-gradient';
import { Image } from 'expo-image';
import { Stack, useLocalSearchParams, useRouter } from 'expo-router';
import ArrowLeft from 'lucide-react-native/icons/arrow-left';
import Calendar from 'lucide-react-native/icons/calendar';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { SafeAreaView } from 'react-native-safe-area-context';
import { useTranslation } from 'react-i18next';

import { ArticleHead } from '@/components/detail/article-head';
import { ArticleRelated } from '@/components/detail/article-related';
import { PortableTextBody } from '@/components/riviera/portable-text-body';
import { SiteFooter } from '@/components/riviera/site-footer';
import { SiteHeader } from '@/components/riviera/site-header';
import { Badge } from '@/components/ui/badge';
import { Fonts, MaxContentWidth, Radius, Spacing } from '@/constants/theme';
import { useArticle } from '@todo/core/hooks/use-article';
import { useBreakpoint } from '@/hooks/use-breakpoint';
import { useTheme } from '@/hooks/use-theme';
import { buildArticleLocaleLinks } from '@/lib/detail/article-detail';
import { articleImageAlt } from '@/lib/i18n/article-content';
import { formatDateMedium } from '@todo/core/i18n/format';
import { LocaleLinksProvider } from '@todo/core/i18n/locale-links-context';
import { useLocale } from '@todo/core/i18n/use-locale';
import { urlForImage } from '@todo/core/sanity/client';
import { type ArticleDetail } from '@todo/core/sanity/queries';
import { getPrefetched } from '@todo/core/static-prefetch';

export { generateArticleStaticParams as generateStaticParams } from '@/lib/detail/article-detail';

export default function ArticleScreen() {
  const theme = useTheme();
  const router = useRouter();
  const { t } = useTranslation();
  const { locale } = useLocale();
  const { isLg } = useBreakpoint();
  const { slug } = useLocalSearchParams<{ slug: string }>();
  const { article: fetchedArticle, loading, error } = useArticle(slug, locale);

  // At build time the client fetch hasn't run; fall back to the record
  // generateStaticParams stashed so meta + body land in the static HTML.
  // Namespaced by locale — Sanity articles genuinely differ per language,
  // unlike events/tours, so a slug isn't a globally unique cache key on its own.
  const prefetchKey = slug ? `${locale}:${slug}` : undefined;
  const article = fetchedArticle ?? getPrefetched<ArticleDetail>('article', prefetchKey) ?? null;

  const heroUri = article?.mainImage
    ? urlForImage(article.mainImage).width(1600).height(900).url()
    : null;
  const localeLinks = buildArticleLocaleLinks(article, locale);

  return (
    <LocaleLinksProvider value={localeLinks}>
      <View style={[styles.root, { backgroundColor: theme.background }]}>
        <Stack.Screen options={{ headerShown: false }} />
        <ArticleHead
          article={article}
          locale={locale}
          slug={slug}
          notFoundTitle={t('articles.notFound')}
        />
        <SafeAreaView edges={['top']} style={{ backgroundColor: theme.card }}>
          <SiteHeader />
        </SafeAreaView>
        <ScrollView
          style={{ flex: 1 }}
          showsVerticalScrollIndicator={false}>
          {article ? (
            <View style={styles.centered}>
              <View style={[styles.page, isLg && styles.pageLg]}>
                <View style={[styles.heroBox, isLg && styles.heroBoxLg]}>
                  {heroUri ? (
                    <Image
                      source={{ uri: heroUri }}
                      style={styles.hero}
                      contentFit="cover"
                      accessibilityLabel={articleImageAlt(article)}
                    />
                  ) : (
                    <View style={[styles.hero, { backgroundColor: theme.muted }]} />
                  )}
                  <LinearGradient
                    colors={['rgba(0,0,0,0.45)', 'rgba(0,0,0,0)']}
                    style={styles.heroScrim}
                  />
                  <Pressable
                    accessibilityRole="button"
                    accessibilityLabel={t('a11y.back')}
                    onPress={() => router.back()}
                    style={({ pressed }) => [styles.backBtn, pressed && { opacity: 0.7 }]}>
                    <ArrowLeft size={20} color="#fff" />
                  </Pressable>
                  <View style={styles.heroBadge}>
                    <Badge variant="gold">{t('articles.badge')}</Badge>
                  </View>
                </View>

                <View
                  style={[
                    styles.card,
                    isLg && styles.cardLg,
                    { backgroundColor: theme.card, borderColor: theme.border },
                  ]}>
                  <Text
                    style={[
                      styles.title,
                      isLg && styles.titleLg,
                      { color: theme.text, fontFamily: Fonts?.serif },
                    ]}>
                    {article.title}
                  </Text>
                  {article.publishedAt && (
                    <View style={styles.metaRow}>
                      <Calendar size={14} color={theme.mutedForeground} />
                      <Text style={[styles.date, { color: theme.mutedForeground }]}>
                        {formatDateMedium(article.publishedAt, locale)}
                      </Text>
                    </View>
                  )}
                  <View style={[styles.divider, { backgroundColor: theme.border }]} />
                  <View style={styles.body}>
                    <PortableTextBody value={article.body} />
                  </View>
                </View>
              </View>
              <ArticleRelated article={article} locale={locale} />
            </View>
          ) : loading ? (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('articles.loadingOne')}
            </Text>
          ) : error ? (
            <Text style={[styles.statusText, { color: theme.destructive }]}>
              {t('articles.loadErrorOne', { error: error.message })}
            </Text>
          ) : (
            <Text style={[styles.statusText, { color: theme.mutedForeground }]}>
              {t('articles.notFound')}
            </Text>
          )}
          <SiteFooter />
        </ScrollView>
      </View>
    </LocaleLinksProvider>
  );
}

const styles = StyleSheet.create({
  root: {
    flex: 1,
  },
  statusText: {
    fontSize: 14,
    paddingHorizontal: Spacing.three,
    paddingVertical: Spacing.four,
  },
  centered: {
    alignItems: 'center',
    width: '100%',
  },
  page: {
    width: '100%',
    maxWidth: MaxContentWidth,
  },
  pageLg: {
    paddingHorizontal: Spacing.four,
    paddingTop: Spacing.four,
  },
  heroBox: {
    width: '100%',
    aspectRatio: 16 / 9,
    position: 'relative',
  },
  heroBoxLg: {
    borderRadius: Radius['2xl'],
    overflow: 'hidden',
  },
  hero: {
    width: '100%',
    height: '100%',
  },
  heroScrim: {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '40%',
  },
  backBtn: {
    position: 'absolute',
    top: Spacing.three,
    left: Spacing.three,
    width: 38,
    height: 38,
    borderRadius: 999,
    backgroundColor: 'rgba(0,0,0,0.35)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  heroBadge: {
    position: 'absolute',
    top: Spacing.three,
    right: Spacing.three,
  },
  card: {
    marginTop: -Spacing.four,
    borderTopLeftRadius: Radius['2xl'],
    borderTopRightRadius: Radius['2xl'],
    borderWidth: 1,
    borderBottomWidth: 0,
    paddingHorizontal: Spacing.three,
    paddingTop: Spacing.four,
    paddingBottom: Spacing.five,
  },
  cardLg: {
    marginTop: -Spacing.five,
    borderRadius: Radius['2xl'],
    borderBottomWidth: 1,
    paddingHorizontal: Spacing.five,
    paddingVertical: Spacing.five,
    maxWidth: 760,
    alignSelf: 'center',
    width: '100%',
  },
  title: {
    fontSize: 26,
    fontWeight: '700',
    lineHeight: 33,
  },
  titleLg: {
    fontSize: 34,
    lineHeight: 41,
  },
  metaRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    marginTop: Spacing.two,
  },
  date: {
    fontSize: 13,
  },
  divider: {
    height: StyleSheet.hairlineWidth,
    marginVertical: Spacing.four,
  },
  body: {
    maxWidth: 720,
  },
});
